import React, { useState, useEffect } from 'react'
import {
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
} from '@mui/material'
import {
  Download as DownloadIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material'
import { agentsAPI } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import AdminPageWrapper from '../components/AdminPageWrapper'

const AdminDataExportPage = () => {
  const { getAccessToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [agentTypes, setAgentTypes] = useState([])
  const [agentsLoading, setAgentsLoading] = useState(true)

  // Form state
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [ticketType, setTicketType] = useState('')

  useEffect(() => {
    fetchAgentTypes()
  }, [])

  const fetchAgentTypes = async () => {
    try {
      setAgentsLoading(true)
      const response = await agentsAPI.getAgents(true, false) // minimal=true, includeSubAgents=false
      const types = (response.agents || []).map(agent => agent.name).sort()
      setAgentTypes(types)
    } catch (err) {
      console.error('Error fetching agent types:', err)
    } finally {
      setAgentsLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      setLoading(true)
      setError('')
      setSuccess('')

      // Build query params
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      if (ticketType) params.append('ticket_type', ticketType)

      // Get the auth token from the auth hook
      const token = await getAccessToken()

      // Fetch the export file
      const url = `/api/v1/admin/export/tickets${params.toString() ? `?${params.toString()}` : ''}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || `Export failed with status ${response.status}`)
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `tickets_export_${new Date().toISOString().slice(0, 10)}.json`
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/)
        if (match) filename = match[1]
      }

      // Download the file
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl)

      setSuccess(`Export downloaded successfully: ${filename}`)
    } catch (err) {
      console.error('Export error:', err)
      setError(err.message || 'Failed to export tickets')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AdminPageWrapper title="Data Export" subtitle="Export ticket data with conversation threads and notes">
      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRangeIcon color="primary" />
              Export Filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a date range and optionally filter by ticket type. The export will include
              the full conversation thread and all user notes for each ticket.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Filter tickets created on or after this date"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Filter tickets created on or before this date"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Ticket Type</InputLabel>
                  <Select
                    value={ticketType}
                    label="Ticket Type"
                    onChange={(e) => setTicketType(e.target.value)}
                    disabled={agentsLoading}
                  >
                    <MenuItem value="">
                      <em>All Types</em>
                    </MenuItem>
                    {agentTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DownloadIcon color="primary" />
              Download Export
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The export will be downloaded as a JSON file containing an array of tickets.
              Each ticket includes its full conversation thread, user notes, and child tickets.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleExport}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              >
                {loading ? 'Exporting...' : 'Export Tickets'}
              </Button>

              {!startDate && !endDate && !ticketType && (
                <Typography variant="body2" color="warning.main">
                  No filters applied - this will export all tickets
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </AdminPageWrapper>
  )
}

export default AdminDataExportPage
